import colorPallete, {
    bodyFonts,
    themeFontWeights,
} from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img/Img";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import styled, { css } from "styled-components";

interface HoverTileModel {
    fields: {
        tileTitle: string;
        tileHoverText: string;
        tileIcon: string;
        titeIconImageAltText: string;
    };
}

const enum fields {
    sectionHeaderTitle = "sectionHeaderTitle",
    sectionHeaderDescription = "sectionHeaderDescription",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    hoverTiles = "hoverTiles",
}

interface Props extends WidgetProps {
    fields: {
        [fields.sectionHeaderTitle]: string;
        [fields.sectionHeaderDescription]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.hoverTiles]: HoverTileModel[];
    };
}

export interface HoverTileStyles {
    hoverTilesWrapper?: InjectableCss;
    sectionHeaderTitle?: TypographyPresentationProps;
    sectionHeaderDescription?: TypographyPresentationProps;
    hoverTile?: InjectableCss;
    tileTitle?: TypographyPresentationProps;
    tileHoverText?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
}

export const hoverTileStyles: HoverTileStyles = {
    hoverTilesWrapper: {
        css: css`
            max-width: 1023px;
            margin: 24px auto;
            column-gap: 40px;
            row-gap: 24px;
            justify-content: center;
            cursor: pointer;
            max-width: 722px;

            display: flex;
            flex-wrap: wrap;
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            row-gap: 33px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    sectionHeaderTitle: {
        variant: "h2",
        css: css`
            margin-bottom: 8px;

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    margin-bottom: 24px;
                `,
                null,
                null,
            ], "max")}
        `,
    },
    sectionHeaderDescription: {
        css: css`
            padding: 0 16px;
            p[class*="TypographyStyle"] {
                margin-bottom: 0;
                line-height: 24px;
            }
        `
    },
    hoverTile: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            border: 1px solid ${colorPallete.grey500};
            border-radius: 8px;
            padding: 35px 15px;
            min-height: 189px;
            max-height: 189px;
            flex-basis: 214px;
            max-width: 214px;
            img {
                margin-bottom: 13px;
            }
            &:hover {
                img,
                h4 {
                    display: none;
                }

                .title-hover-text {
                    display: block;
                }
            }


            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 25px 15px;
                            min-height: 192px;
                            max-height: 192px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `
    },
    tileTitle: {
        variant: "h4",
        weight: themeFontWeights.semiBold,
        css: css`
            margin-bottom: 0;
        `
    },
    tileHoverText: {
        css: css`
            display: none;

            p[class*="TypographyStyle"] {
                font-size: ${bodyFonts.body1RegularLg.fontSize};
                font-family: ${bodyFonts.body1RegularLg.fontFamily};
                line-height: ${bodyFonts.body1RegularLg.lineHeight};
                font-weight: ${bodyFonts.body1RegularLg.fontWeight};
                color: ${colorPallete.grey800};
                text-align: center;
                margin-bottom: 0;

                [class*="StyledClickable"] {
                    color: ${colorPallete.primaryRed500};
                    text-decoration: underline;
                    
                    span {
                        color: ${colorPallete.primaryRed500};
                    }
                }
            }
        `
    },
};

const HoverTilesSectionWrapper = styled.div`
    max-width: 1280px;
    margin: 0 auto;
    padding: 48px 0;
    text-align: center;
    height: 100%;
    @media only screen and (max-width: 768px) {
        padding: 40px 0;
    }
`;


const DsgHoverTiles: React.FC<Props> = props => {
    const styles = hoverTileStyles;
    const gridGap = 40;

    return (
        <HoverTilesSectionWrapper>
            <StyledWrapper className="hover-section-header">
                <Typography {...styles.sectionHeaderTitle}>{props.fields.sectionHeaderTitle}</Typography>
                <Typography forwardAs="div" {...styles.sectionHeaderDescription}>
                    {parse(props.fields.sectionHeaderDescription, parserOptions)}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.hoverTilesWrapper}>
                {props.fields.hoverTiles &&
                    props.fields.hoverTiles.map((hoverTile, index) => (
                        <StyledWrapper {...styles.hoverTile} className="hover-Tile" key={`hoverTile-${index}${Math.random()}`}>
                            <Img src={hoverTile.fields.tileIcon} altText={hoverTile.fields.titeIconImageAltText || ''}/>
                            <Typography {...styles.tileTitle}>{hoverTile.fields.tileTitle}</Typography>
                            <Typography as="div" {...styles.tileHoverText} className="title-hover-text">
                                {parse(hoverTile.fields.tileHoverText, parserOptions)}
                            </Typography>
                        </StyledWrapper>
                    ))}
            </StyledWrapper>
            {props.fields[fields.buttonLabel] !== "" && props.fields[fields.buttonLink].value !== "" && (
                <Link href={props.fields.buttonLink.value} className="text-link text-link-primary">
                    {props.fields.buttonLabel}
                </Link>
            )}
        </HoverTilesSectionWrapper>
    );
};

const basicTab = {
    displayName: "Common",
    sortOrder: 0,
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.sectionHeaderTitle,
            displayName: "Section Header",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "Enter Title",
            tab: basicTab,
        },
        {
            name: fields.sectionHeaderDescription,
            fieldType: "Translatable",
            editorTemplate: "RichTextField",
            displayName: "Section Header Description",
            defaultValue: "",
            extendedConfig: { height: 170 },
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
            tab: basicTab,
        },
        {
            name: fields.buttonLabel,
            editorTemplate: "TextField",
            fieldType: "General",
            displayName: "Button Label",
            defaultValue: "",
            tab: basicTab,
        },
        {
            name: fields.buttonLink,
            displayName: "Button Link",
            fieldType: "General",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
            tab: basicTab,
        },
        {
            name: fields.hoverTiles,
            editorTemplate: "ListField",
            tab: basicTab,
            getDisplay: ({ fields: { tileTitle } }) => tileTitle || "Tiles",
            defaultValue: [],
            fieldType: "Contextual",
            fieldDefinitions: [
                {
                    name: "tileTitle",
                    displayName: "Tile Title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    tooltip: "text tip",
                    placeholder: "Enter Title",
                    tab: basicTab,
                },
                {
                    name: "tileHoverText",
                    editorTemplate: "RichTextField",
                    displayName: "Tile Hover Text",
                    defaultValue: "",
                    extendedConfig: { height: 170 },
                    expandedToolbarButtons: {
                        moreMisc: {
                            buttons: [
                                "insertLink",
                                "insertImage",
                                "insertHR",
                                "specialCharacters",
                                "insertVideo",
                                "insertFile",
                            ],
                            buttonsVisible: 3,
                        },
                        code: {
                            buttons: ["selectAll", "html", "help"],
                            buttonsVisible: 3,
                        },
                    },
                    tab: basicTab,
                },
                {
                    name: "tileIcon",
                    displayName: "Tile Icon",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                    tab: basicTab,
                },
                {
                    name: "titeIconImageAltText",
                    displayName: "Tile Icon Image Alt Text",
                    editorTemplate: "TextField",
                    defaultValue: "",
                }
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: DsgHoverTiles,
    definition,
};

export default widgetModule;
